import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Calendar, Globe, FileText, Download, ArrowLeft, Share2,
  Copy, Loader2, Clock, Users, Eye, CheckCircle, XCircle,
  Megaphone, Building2, ExternalLink, AlertCircle, File, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  trade_mission: { label: 'Trade Mission', color: 'bg-blue-500/20 text-blue-400' },
  investment_opportunity: { label: 'Investment Opportunity', color: 'bg-emerald-500/20 text-emerald-400' },
  product_launch: { label: 'Product Launch', color: 'bg-purple-500/20 text-purple-400' },
  delegation_visit: { label: 'Delegation Visit', color: 'bg-amber-500/20 text-amber-400' },
  partnership: { label: 'Partnership', color: 'bg-cyan-500/20 text-cyan-400' },
  exhibition_trade_show: { label: 'Exhibition / Trade Show', color: 'bg-rose-500/20 text-rose-400' },
  announcement: { label: 'Announcement', color: 'bg-orange-500/20 text-orange-400' },
  pharma_park: { label: 'Pharma Park / SEZ', color: 'bg-teal-500/20 text-teal-400' },
  infrastructure: { label: 'Infrastructure Project', color: 'bg-indigo-500/20 text-indigo-400' },
  other: { label: 'Opportunity', color: 'bg-zinc-500/20 text-zinc-400' },
};

export default function PublicOpportunityPage() {
  const { slug, oppSlug } = useParams();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRSVP, setShowRSVP] = useState(null); // 'interested' | 'declined' | null
  const isLeadership = window.location.pathname.startsWith('/l/');
  const profilePrefix = isLeadership ? '/l' : '/p';

  useEffect(() => {
    axios.get(`${API_URL}/api/p/${slug}/opportunity/${oppSlug}`)
      .then(r => setOpp(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Opportunity not found'))
      .finally(() => setLoading(false));
  }, [slug, oppSlug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${opp.title} — ${window.location.href}`)}`, '_blank');
  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank');
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent(opp.title)}&body=${encodeURIComponent(`Check out this opportunity: ${window.location.href}`)}`, '_blank');

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-white mb-2">Not Found</h2>
        <p className="text-zinc-400 mb-4">{error}</p>
        <Link to={`${profilePrefix}/${slug}`} className="text-[#C41E3A] hover:underline">View Profile</Link>
      </div>
    </div>
  );

  const catMeta = CATEGORY_META[opp.category] || CATEGORY_META.other;
  const deadline = opp.key_dates?.deadline;
  const eventDate = opp.key_dates?.event_date;
  const isExpired = deadline && new Date(deadline) < new Date();
  const inviterDisplay = opp.inviter_name ? `${opp.inviter_title || ''} ${opp.inviter_name}`.trim() : '';

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="public-opportunity-page">
      {/* Branded Header — "Invitation from [Company]" */}
      <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B1429]" data-testid="invitation-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {opp.profile_logo ? (
              <img src={opp.profile_logo} alt={opp.profile_name} className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover border-2 border-white/20" />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white/80" />
              </div>
            )}
            <div>
              <p className="text-white/70 text-xs sm:text-sm">Invitation from</p>
              <p className="text-white font-bold text-lg sm:text-xl">{opp.profile_name}</p>
              {inviterDisplay && (
                <p className="text-white/60 text-xs mt-0.5">by {inviterDisplay}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={copyLink} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Copy link" data-testid="copy-link-btn">
              <Copy className="w-4 h-4 text-white/80" />
            </button>
            <button onClick={shareWhatsApp} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="WhatsApp">
              <Share2 className="w-4 h-4 text-green-300" />
            </button>
            <button onClick={shareLinkedIn} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="LinkedIn">
              <ExternalLink className="w-4 h-4 text-blue-300" />
            </button>
            <button onClick={shareEmail} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Email">
              <Mail className="w-4 h-4 text-white/80" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        {/* Category + Status badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${catMeta.color}`}>
            {catMeta.label}
          </span>
          {opp.geography && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-zinc-800 text-zinc-300">
              <Globe className="w-3 h-3" /> {opp.geography}
            </span>
          )}
          {isExpired && (
            <span className="px-2.5 py-1 rounded-full text-xs bg-red-500/20 text-red-400 font-bold">DEADLINE PASSED</span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight mb-5" data-testid="opportunity-title">
          {opp.title}
        </h1>

        {/* Date / Time / Venue cards (like the screenshot) */}
        {(eventDate || deadline || opp.location) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {eventDate && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Calendar className="w-4 h-4 text-[#C41E3A]" /> Date
                </div>
                <p className="text-white font-semibold">
                  {new Date(eventDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            )}
            {deadline && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Clock className="w-4 h-4 text-amber-400" /> Response Deadline
                </div>
                <p className={`font-semibold ${isExpired ? 'text-red-400' : 'text-white'}`}>
                  {new Date(deadline).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            )}
            {opp.location && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <MapPin className="w-4 h-4 text-[#C41E3A]" /> Venue
                </div>
                <p className="text-white font-semibold">{opp.location}</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="opportunity-overview">
              <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-[#C41E3A]" /> About This Opportunity
              </h2>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{opp.overview}</p>
            </div>

            {/* Detail Sections */}
            {(opp.sections || []).filter(s => s.title || s.content).map((section, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-base font-bold text-white mb-3">{section.title}</h2>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{section.content}</p>
              </div>
            ))}

            {/* Documents */}
            {opp.documents && opp.documents.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="opportunity-documents">
                <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#C41E3A]" /> Supporting Documents
                </h2>
                <div className="space-y-2">
                  {opp.documents.map((doc, i) => (
                    <button
                      key={i}
                      onClick={async () => {
                        const btn = document.querySelector(`[data-testid="doc-download-${i}"]`);
                        const statusEl = btn?.querySelector('[data-role="status"]');
                        try {
                          if (statusEl) statusEl.textContent = 'Downloading...';
                          const proxyUrl = `${API_URL}/api/documents/download?url=${encodeURIComponent(doc.url)}&filename=${encodeURIComponent(doc.name || 'document')}`;
                          const resp = await fetch(proxyUrl);
                          if (!resp.ok) {
                            const errData = await resp.json().catch(() => ({}));
                            throw new Error(errData.detail || 'Download failed');
                          }
                          const blob = await resp.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = blobUrl;
                          a.download = doc.name || 'document';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(blobUrl);
                          if (statusEl) statusEl.textContent = '';
                        } catch (err) {
                          if (statusEl) statusEl.textContent = '';
                          alert(err.message || 'Unable to download document. Please try again.');
                        }
                      }}
                      className="flex items-center gap-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg px-4 py-3 transition-colors group w-full text-left cursor-pointer"
                      data-testid={`doc-download-${i}`}
                    >
                      <File className="w-5 h-5 text-zinc-500 group-hover:text-[#C41E3A] transition-colors shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{doc.name}</p>
                        <p className="text-zinc-500 text-xs">
                          {(doc.size / 1024).toFixed(0)} KB
                          <span data-role="status" className="ml-2 text-amber-400"></span>
                        </p>
                      </div>
                      <Download className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Share Section */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-base font-bold text-white mb-4">Share This Invitation</h2>
              <div className="flex flex-wrap gap-2">
                <button onClick={copyLink} className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                  <Copy className="w-4 h-4" /> Copy Link
                </button>
                <button onClick={shareWhatsApp} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                  <Share2 className="w-4 h-4" /> WhatsApp
                </button>
                <button onClick={shareLinkedIn} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                  <ExternalLink className="w-4 h-4" /> LinkedIn
                </button>
                <button onClick={shareEmail} className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                  <Mail className="w-4 h-4" /> Email
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* RSVP CTA Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sticky top-6" data-testid="rsvp-sidebar">
              {/* Company identity in sidebar too */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-zinc-800">
                {opp.profile_logo ? (
                  <img src={opp.profile_logo} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-zinc-500" />
                  </div>
                )}
                <div>
                  <p className="text-white text-sm font-semibold">{opp.profile_name}</p>
                  {inviterDisplay && <p className="text-zinc-500 text-xs">by {inviterDisplay}</p>}
                </div>
              </div>

              <h3 className="text-white font-bold mb-1">RSVP</h3>
              <p className="text-zinc-400 text-sm mb-4">Respond to this invitation</p>

              {!isExpired ? (
                <div className="space-y-2">
                  <button
                    onClick={() => setShowRSVP('interested')}
                    className="w-full bg-[#C41E3A] hover:bg-[#a31830] text-white font-bold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                    data-testid="express-interest-btn"
                  >
                    <CheckCircle className="w-4 h-4" /> Express Interest
                  </button>
                  <button
                    onClick={() => setShowRSVP('declined')}
                    className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                    data-testid="decline-btn"
                  >
                    <XCircle className="w-4 h-4" /> Decline
                  </button>
                </div>
              ) : (
                <div className="text-center py-3 bg-zinc-800 rounded-lg text-zinc-500 text-sm">
                  Response deadline has passed
                </div>
              )}

              {/* Contact */}
              {(opp.contact_name || opp.contact_email) && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Contact</p>
                  {opp.contact_name && <p className="text-white text-sm font-medium">{opp.contact_name}</p>}
                  {opp.contact_email && <a href={`mailto:${opp.contact_email}`} className="text-[#C41E3A] text-sm hover:underline">{opp.contact_email}</a>}
                  {opp.contact_phone && <p className="text-zinc-400 text-sm">{opp.contact_phone}</p>}
                </div>
              )}

              {/* Profile Link */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <Link
                  to={`${profilePrefix}/${slug}`}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors"
                >
                  <Building2 className="w-4 h-4" />
                  View {opp.profile_name} Profile
                </Link>
              </div>

              {/* View Count */}
              <div className="mt-3 text-center">
                <span className="text-zinc-600 text-xs flex items-center justify-center gap-1">
                  <Eye className="w-3 h-3" /> {opp.view_count} views
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RSVP Modals */}
      {showRSVP === 'interested' && (
        <RSVPModal slug={slug} oppSlug={oppSlug} oppTitle={opp.title} profileName={opp.profile_name} inviterDisplay={inviterDisplay} rsvpStatus="interested" onClose={() => setShowRSVP(null)} />
      )}
      {showRSVP === 'declined' && (
        <DeclineModal slug={slug} oppSlug={oppSlug} oppTitle={opp.title} profileName={opp.profile_name} onClose={() => setShowRSVP(null)} />
      )}

      {/* Footer */}
      <div className="text-center py-6 border-t border-zinc-800">
        <Link to="/" className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors">
          Powered by BrandStage
        </Link>
      </div>
    </div>
  );
}

// ---- Express Interest Modal (Full Form + Confirmation Email) ----
function RSVPModal({ slug, oppSlug, oppTitle, profileName, inviterDisplay, onClose }) {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', designation: '', message: '', marketing_consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()) {
      alert('Name, company, and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/p/${slug}/opportunity/${oppSlug}/respond`, {
        ...form,
        rsvp_status: 'interested'
      });
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit. Please try again.');
    }
    setSubmitting(false);
  };

  if (success) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 max-w-md w-full text-center" data-testid="rsvp-success">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">RSVP Confirmed!</h3>
        <p className="text-zinc-400 text-sm mb-2">
          Your interest in <strong className="text-white">"{oppTitle}"</strong> has been registered.
        </p>
        <div className="bg-zinc-800/50 rounded-lg p-3 mb-4 flex items-center gap-2 justify-center">
          <Mail className="w-4 h-4 text-[#C41E3A]" />
          <span className="text-zinc-300 text-sm">A confirmation email has been sent to <strong className="text-white">{form.email}</strong></span>
        </div>
        <p className="text-zinc-500 text-xs mb-6">
          {profileName} will be in touch shortly with further details.
        </p>
        <button onClick={onClose} className="bg-[#C41E3A] hover:bg-[#a31830] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">
          Done
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="rsvp-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold">Express Interest</h3>
            {inviterDisplay && <p className="text-zinc-500 text-xs">Invited by {inviterDisplay}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded">
            <span className="text-zinc-400 text-xl leading-none">&times;</span>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Full Name *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" required data-testid="eoi-name" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Company *</label>
              <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" required data-testid="eoi-company" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Work Email *</label>
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" required data-testid="eoi-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Designation</label>
              <input value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Message</label>
            <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={3} placeholder="Tell us about your interest..." className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C41E3A] focus:outline-none resize-none" data-testid="eoi-message" />
          </div>
          <label className="flex items-start gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={form.marketing_consent} onChange={e => setForm(p => ({ ...p, marketing_consent: e.target.checked }))} className="mt-0.5 accent-[#C41E3A]" />
            <span>I consent to receiving updates about this and similar opportunities. <a href="/privacy" target="_blank" className="text-[#C41E3A] hover:underline">Privacy Policy</a></span>
          </label>
          <button type="submit" disabled={submitting} className="w-full bg-[#C41E3A] hover:bg-[#a31830] disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2" data-testid="submit-eoi-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {submitting ? 'Submitting...' : 'Confirm Interest'}
          </button>
          <p className="text-zinc-600 text-[10px] text-center">You will receive a confirmation email upon submission.</p>
        </form>
      </div>
    </div>
  );
}

// ---- Decline Modal (Quick — just name + email) ----
function DeclineModal({ slug, oppSlug, oppTitle, profileName, onClose }) {
  const [form, setForm] = useState({ name: '', company: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleDecline = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      alert('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/p/${slug}/opportunity/${oppSlug}/respond`, {
        ...form,
        company: form.company || 'N/A',
        rsvp_status: 'declined'
      });
      setDone(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit.');
    }
    setSubmitting(false);
  };

  if (done) return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 max-w-md w-full text-center" data-testid="decline-success">
        <XCircle className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Response Noted</h3>
        <p className="text-zinc-400 text-sm mb-6">Thank you for letting us know. We hope to see you at a future event.</p>
        <button onClick={onClose} className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="decline-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
          <h3 className="text-white font-bold text-sm">Decline Invitation</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded">
            <span className="text-zinc-400 text-xl leading-none">&times;</span>
          </button>
        </div>
        <form onSubmit={handleDecline} className="p-6 space-y-3">
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name *" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-zinc-500 focus:outline-none" required />
          <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Company (optional)" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-zinc-500 focus:outline-none" />
          <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-zinc-500 focus:outline-none" required />
          <button type="submit" disabled={submitting} className="w-full bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-300 font-medium py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2" data-testid="confirm-decline-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Confirm Decline
          </button>
        </form>
      </div>
    </div>
  );
}
